import {
  Avatar,
  Box,
  Divider,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { useNavigate } from '@tanstack/react-router';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { reportError } from '../../../../shared/utils/report-error';
import { useAuthContext, UserProfile } from '../../../auth';
import {
  getAdminNavigationItems,
  getBottomNavigationItems,
  getNavigationItems,
} from '../navigation/items';
import { filterItemsByPermission } from '../navigation/utils';
import {
  avatarCompactSx,
  avatarSx,
  menuItemSx,
  menuPaperSx,
  spacerSx,
  textContentSx,
  userNameSx,
  userProfileCompactContainerSx,
  userProfileContainerSx,
  userRoleSx,
} from './styles';
import { getFullName, getInitials, getRoleText } from './utils';

export const UserProfileDropdown: React.FC<{
  anchorOrigin?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
  transformOrigin?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
  isCompact?: boolean;
  showNavigationItems?: boolean;
  containerSx?: SxProps<Theme>;
  menuOffsetY?: string;
}> = ({
  anchorOrigin,
  transformOrigin,
  isCompact = false,
  showNavigationItems = false,
  containerSx: containerSxOverride,
  menuOffsetY = '-22px',
}) => {
  const { user, logout, getUserProfile } = useAuthContext();
  const { t } = useTranslation(['common']);
  const navigate = useNavigate();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const menuOpen = Boolean(menuAnchorEl);

  // Fetch user profile to get firstName and lastName
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user || typeof getUserProfile !== 'function') return;
      try {
        const profile = await getUserProfile();
        setUserProfile(profile);
      } catch (error) {
        reportError('Failed to fetch user profile', error);
      }
    };
    fetchProfile();
  }, [user, getUserProfile]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    try {
      await logout();
    } catch (error) {
      reportError('Logout failed', error);
    }
  };

  if (!user) return null;

  const containerSx = isCompact
    ? userProfileCompactContainerSx
    : userProfileContainerSx;
  const avatarStyle: SxProps<Theme> = isCompact
    ? [avatarSx, avatarCompactSx]
    : avatarSx;

  const navigationItems = showNavigationItems
    ? [
        ...filterItemsByPermission(getNavigationItems(t), user),
        ...filterItemsByPermission(getAdminNavigationItems(t), user),
        ...filterItemsByPermission(getBottomNavigationItems(), user),
      ]
    : [];

  const baseContainerSx = Array.isArray(containerSx)
    ? containerSx
    : [containerSx];
  const mergedContainerSx: SxProps<Theme> = containerSxOverride
    ? [
        ...baseContainerSx,
        ...(Array.isArray(containerSxOverride)
          ? containerSxOverride
          : [containerSxOverride]),
      ]
    : baseContainerSx;

  const handleNavigate = (to: string) => {
    handleMenuClose();
    navigate({ to });
  };

  return (
    <>
      <Box onClick={handleClick} sx={mergedContainerSx}>
        {/* Avatar */}
        <Avatar sx={avatarStyle}>{getInitials(user, userProfile)}</Avatar>

        {!isCompact && (
          <>
            {/* Spacing */}
            <Box sx={spacerSx} />

            {/* Text Content */}
            <Box sx={textContentSx}>
              {/* Full Name */}
              <Typography sx={userNameSx}>
                {getFullName(user, userProfile)}
              </Typography>

              {/* Role */}
              <Typography sx={userRoleSx}>{getRoleText(user)}</Typography>
            </Box>
          </>
        )}
      </Box>

      {/* Dropdown Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        sx={{
          transform: `translateY(${menuOffsetY})`,
        }}
        anchorOrigin={
          anchorOrigin ?? {
            vertical: 'bottom',
            horizontal: 'right',
          }
        }
        transformOrigin={
          transformOrigin ?? {
            vertical: 'top',
            horizontal: 'right',
          }
        }
        slotProps={{
          paper: {
            sx: {
              ...menuPaperSx,
            },
          },
        }}
      >
        {navigationItems.map((item) => (
          <MenuItem
            key={item.to}
            onClick={() => handleNavigate(item.to)}
            sx={menuItemSx}
          >
            {item.label}
          </MenuItem>
        ))}

        {navigationItems.length > 0 && <Divider />}

        <MenuItem onClick={handleLogout} sx={menuItemSx}>
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '12px',
              color: 'rgba(0, 0, 0, 0.87)',
              fontSize: '16px',
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M17 7L15.59 8.41L18.17 11H8V13H18.17L15.59 15.58L17 17L22 12L17 7ZM4 5H12V3H4C2.9 3 2 3.9 2 5V19C2 20.1 2.9 21 4 21H12V19H4V5Z"
                fill="currentColor"
                fillOpacity="0.56"
              />
            </svg>
            Logout
          </Box>
        </MenuItem>
      </Menu>
    </>
  );
};
